import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { X } from "lucide-react";
import { api } from "@/lib/api";

let _stripePromise = null;
function getStripe(publishableKey) {
  if (!publishableKey) return null;
  if (!_stripePromise) _stripePromise = loadStripe(publishableKey);
  return _stripePromise;
}

function PaymentForm({ onSuccess, onClose }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setErr("");
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (error) {
      setErr(error.message || "Payment failed");
      setSubmitting(false);
      return;
    }
    setSubmitting(false);
    onSuccess?.();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement options={{ sandboxAssistant: { enabled: false } }} />
      {err && (
        <div className="text-[13px] text-red-600" data-testid="subscribe-error">
          {err}
        </div>
      )}
      <div className="flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onClose}
          className="btn text-[13px] py-1.5 px-3"
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn-primary text-[13px] py-1.5 px-4"
          disabled={!stripe || submitting}
          data-testid="subscribe-submit"
        >
          {submitting ? "Processing…" : "Subscribe"}
        </button>
      </div>
    </form>
  );
}

export default function SubscribeModal({ open, onClose, onSuccess }) {
  const [config, setConfig] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [loadErr, setLoadErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setClientSecret("");
      setLoadErr("");
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const { data: cfg } = await api.get("/billing/config");
        if (cancelled) return;
        if (!cfg.configured) {
          setLoadErr("Billing is not configured yet. Please contact support.");
          setLoading(false);
          return;
        }
        setConfig(cfg);
        const { data } = await api.post("/billing/subscribe");
        if (cancelled) return;
        if (!data.client_secret) {
          setLoadErr("Could not initialize payment. Try again later.");
          setLoading(false);
          return;
        }
        setClientSecret(data.client_secret);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || e.message || "Could not start subscription.";
        setLoadErr(typeof msg === "string" ? msg : "Could not start subscription.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const stripePromise = config ? getStripe(config.publishable_key) : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid="subscribe-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 text-neutral-500 hover:text-black"
          aria-label="Close"
        >
          <X size={18} />
        </button>
        <h2 className="font-display text-xl font-bold tracking-tight mb-1">
          Subscribe
        </h2>
        <p className="text-[13px] text-neutral-600 mb-5">
          Monthly subscription. Cancel anytime.
        </p>

        {loading && (
          <div className="text-[13px] text-neutral-600 py-6 text-center">
            Preparing payment form…
          </div>
        )}
        {loadErr && (
          <div className="text-[13px] text-red-600 py-2">{loadErr}</div>
        )}
        {clientSecret && stripePromise && (
          <Elements
            stripe={stripePromise}
            options={{ clientSecret, appearance: { theme: "stripe" } }}
          >
            <PaymentForm onSuccess={onSuccess} onClose={onClose} />
          </Elements>
        )}
      </div>
    </div>
  );
}
